"use client";

import React from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import { GroqImage } from "@/helpers/Image";

import { PortableText } from "@portabletext/react";
import {
  customRichTextComponent,
  richTextComponent,
} from "@/sanity/sanityPortableText";

import { CTAButton } from "@/shadcn/ui/button";

import type { PageAffiliateSchema } from "@/sanity/pages/PageAffiliateTemplate";
import ChipSerif from "@/components/global/ui/ChipSerif";
import { PageAffiliateWorkshopSchema } from "@/sanity/pages/PageAffiliateWorkshopTemplate";
import { cn } from "@/lib/utils";

export default function TestimonialSection({
  data,
}: {
  data: PageAffiliateWorkshopSchema;
}) {
  return (
    <section
      id="Testimonial"
      className="w-full h-full lg:px-0 py-[5rem] px-[2rem] lg:py-[15rem]"
    >
      <div className="relative mx-auto w-full">
        <div className="relative mx-auto w-full z-[1] flex flex-col items-center">
          {data?.testimonialsChip && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              <ChipSerif text={data.testimonialsChip} className="mb-0" />
            </motion.div>
          )}
          <motion.div
            className="richtext richtext-center prose-p:text-[1.75rem] lg:prose-p:text-[2rem] prose-p:w-[min(66.625rem,100%)] mb-[5rem] lg:mb-[14rem]"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            {data?.testimonialsTitle && (
              <PortableText
                value={data?.testimonialsTitle}
                components={richTextComponent}
              />
            )}
          </motion.div>

          <div className="w-[min(142.375rem,100%)] flex flex-col gap-[6rem] lg:gap-[22rem]">
            {data.testimonialsList &&
              data.testimonialsList.length > 0 &&
              data.testimonialsList.map((item, index) => (
                <>
                  <div
                    key={"testimonial" + index}
                    className={cn(
                      "flex flex-col items-center gap-[12rem]",
                      index % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse",
                    )}
                  >
                    <div className="w-[min(60.75rem,100%)] flex flex-col items-center lg:items-start">
                      <ChipSerif text={item.chip} className="" />
                      {item.title && (
                        <div className="richtext richtext-center prose-headings:text-[4rem] lg:prose-headings:text-[4.5rem] prose-p:text-[1.75rem] lg:prose-p:text-[2rem] prose-p:w-[min(66.625rem,100%)] mb-[3rem] ">
                          <PortableText
                            value={item.title}
                            components={richTextComponent}
                          />
                        </div>
                      )}
                      <div className="w-[min(69.625rem,100%)] lg:hidden mb-[2rem]">
                        <Image
                          src={item.image}
                          alt=""
                          className="!w-full rounded-[3rem]"
                        />
                      </div>
                      {item.text && (
                        <div className="richtext richtext-start prose-p:text-[1.75rem] lg:prose-p:text-[2rem] prose-p:w-[min(66.625rem,100%)] prose-p:text-[#103037]/70">
                          <PortableText
                            value={item.text}
                            components={richTextComponent}
                          />
                        </div>
                      )}
                    </div>
                    <div className="w-[min(69.625rem,100%)] hidden lg:block">
                      <Image
                        src={item.image}
                        alt=""
                        className="!w-full rounded-[3rem]"
                      />
                    </div>
                  </div>

                  {index !== data.testimonialsList.length - 1 && (
                    <hr className="lg:hidden w-full border-t border-black/10" />
                  )}
                </>
              ))}
          </div>
        </div>
      </div>
    </section>
  );
}
